import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IoIosAdd,
  IoIosArrowForward,
  IoIosWarning,
  IoMdShare,
  IoMdTrash,
} from 'react-icons/io';
import { useDispatch, useSelector } from 'react-redux';
import { ReactQueryKeys } from 'constants/react-query-keys';
import { sortConcentrations, sortTherapies } from 'helpers/drugs';
import { CareArea } from 'interfaces/care-area';
import { Facility } from 'interfaces/facility';
import DrugService from 'services/DrugService';
import {
  deviceActions,
  selectExpandedDrugs,
  selectExpandedTherapies,
} from 'store/slices/device';
import {
  DrugConcentration,
  GetDrugsWithConcentrationsRecord,
} from 'interfaces/drug-concentration';
import SwalAlert, {
  firePreConfirmAlert,
} from 'components/UI/SwalAlert/SwalAlert';
import { SweetAlertResult } from 'sweetalert2';
import { toast } from 'react-hot-toast';
import { Drug } from 'interfaces/drug';
import useBlockerExceptionalCases from 'hooks/useBlockerExceptionalCases';
import ReactTooltip from 'react-tooltip';
import { parseErrorMessage } from 'helpers/parse-error-message';
import { ConcentrationTypesEnum } from 'constants/drugs';
import ListPagination from 'components/UI/ListPagination/ListPagination';
import { ColumnFiltersState } from '@tanstack/react-table';
import DebouncedInput from 'components/UI/DebouncedInput/DebouncedInput';

type ConcentrationsChildren = {
  group: boolean;
  hasAssignedTherapy?: boolean;
  drug_name: GetDrugsWithConcentrationsRecord['name'];
  calculated: GetDrugsWithConcentrationsRecord['concentrations'][0]['name'];
} & GetDrugsWithConcentrationsRecord['concentrations'][0];

interface CommonFeaturesGroupedAndUniqueConc extends Pick<
  GetDrugsWithConcentrationsRecord,
  'is_basic'
> {
  index: number;
  drug_id: GetDrugsWithConcentrationsRecord['id'];
  list_concentration_id: GetDrugsWithConcentrationsRecord['concentrations'][0]['list_concentration_id'];
}

// Grouped has 3 keys: 'type' + 'list_drug_id' + 'calculated'. Unique DOES NOT
interface GroupedConcentrations extends CommonFeaturesGroupedAndUniqueConc {
  group: true;
  children: ConcentrationsChildren[];
  list_drug_id: GetDrugsWithConcentrationsRecord['list_drug_id'];
  calculated: GetDrugsWithConcentrationsRecord['concentrations'][0]['name'];
  type: GetDrugsWithConcentrationsRecord['concentrations'][0]['type'];
}
interface UniqueConcentrations extends CommonFeaturesGroupedAndUniqueConc {
  group: false;
  children: ConcentrationsChildren[];
}

type GroupedAndUniqueConcentrations =
  | UniqueConcentrations
  | GroupedConcentrations;

interface ProcessedDrugsWithConcentrations extends Pick<
  GetDrugsWithConcentrationsRecord,
  'id' | 'is_active' | 'is_basic' | 'list_drug_id' | 'name' | 'shared'
> {
  is_complete: boolean;
  is_complete_therapy: boolean;
  concentrations: GroupedAndUniqueConcentrations[];
}

type DrugsProps = {
  disabledAdding: boolean;
  facility: Facility | null;
  careArea: CareArea | null;
  drugConcentration: DrugConcentration | null;
  drug: Drug | null;
};

/**
 * @description Count duplicate concentrations per drug.
 * Groups concentrations by list concentration id to identify duplicates.
 *
 * @SADtrace SAD-003-4.1, SAD-003-4.3
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {GetDrugsWithConcentrationsRecord['concentrations']} concentrations - Array of concentrations to analyze
 *
 * @returns {Record<string, number>} Object mapping concentration IDs to their duplicate counts
 */
const getConcentrationDuplicateCount = (
  concentrations: GetDrugsWithConcentrationsRecord['concentrations'],
) => {
  const duplicates: Record<string, number> = {};

  // Group by list concentration id for duplicates
  concentrations.forEach((concentration) => {
    duplicates[String(concentration?.list_concentration_id)] =
      (duplicates[String(concentration.list_concentration_id)] || 0) + 1;
  });

  return duplicates;
};

const DELETE_TEST_ID_ROOT = 'delete-concentration-';
const UPDATE_CONCENTRATION_TEST_ID_ROOT = 'update-selected-concentration-test-';
const PAGE_INDEX_DEFAULT_VALUE = 0;

/**
 * @description Drugs component that displays and manages drugs with their concentrations and therapies for a care area.
 * Provides functionality for searching, pagination, expanding/collapsing, selecting, adding, and deleting drugs and concentrations.
 *
 * @SADtrace SAD-003-4.1, SAD-003-4.2, SAD-003-4.3
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {DrugsProps} props - Component props
 *
 * @returns {JSX.Element} Rendered component
 */
const Drugs: FC<DrugsProps> = ({
  disabledAdding,
  facility,
  careArea,
  drugConcentration,
  drug,
}) => {
  const { t } = useTranslation();
  const handleBlock = useBlockerExceptionalCases();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const expandedDrugs: number[] = useSelector(selectExpandedDrugs);
  const expandedTherapies: number[] = useSelector(selectExpandedTherapies);
  const isDrugExpanded = (id: number | string) => expandedDrugs.includes(+id);
  const isTherapyExpanded = (id: number | string) =>
    expandedTherapies.includes(+id);
  const [pagination, setPagination] = useState({
    pageIndex: PAGE_INDEX_DEFAULT_VALUE,
    pageSize: 20,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  /**
   * @description Reset pagination and search filter when different care area is selected
   */
  useEffect(() => {
    setColumnFilters([]);
    setPagination((oldValue) => ({
      ...oldValue,
      pageIndex: PAGE_INDEX_DEFAULT_VALUE,
    }));
  }, [careArea]);

  /**
   * @description Reset drug screen state when pagination changes
   * Clears selected drug, concentration, and expanded items
   */
  const resetDrugScreenOnPaginationChange = () => {
    dispatch(deviceActions.clearSelectedDrugConcentration());
    dispatch(deviceActions.clearSelectedDrug());
    dispatch(deviceActions.clearExpandedDrugs());
    dispatch(deviceActions.clearExpandedTherapies());
  };

  const { data } = useQuery({
    queryKey: [
      ReactQueryKeys.DRUGS,
      {
        ...pagination,
        columnFilters,
        facility,
        careArea,
      },
    ],

    queryFn: async () => {
      const drugs = await DrugService.getDrugsWithConcentrations({
        facilityId: Number(facility?.id),
        careAreaId: Number(careArea?.id),
        ...pagination,
        columnFilters,
      });

      const result = drugs.data.records.map(
        (drug): ProcessedDrugsWithConcentrations => {
          const concentrations = sortConcentrations(
            drug.concentrations,
          ) as GetDrugsWithConcentrationsRecord['concentrations'];

          const groupedConcentrations: GroupedAndUniqueConcentrations[] = [];

          const concentrationDuplicateCount =
            getConcentrationDuplicateCount(concentrations);

          concentrations.forEach((concentration, index) => {
            if (
              concentrationDuplicateCount[
                String(
                  concentration.list_concentration_id,
                ) as keyof typeof concentrationDuplicateCount
              ]
            ) {
              // Group in therapies
              if (
                concentrationDuplicateCount[
                  String(
                    concentration.list_concentration_id,
                  ) as keyof typeof concentrationDuplicateCount
                ] > 1
              ) {
                const groupConcentrations = concentrations.filter(
                  (c) =>
                    String(concentration.list_concentration_id) ===
                    String(c.list_concentration_id),
                );

                groupedConcentrations.push({
                  group: true,
                  index,
                  drug_id: drug.id,
                  list_drug_id: groupConcentrations[0].list_drug_id,
                  type: groupConcentrations[0].type,
                  list_concentration_id:
                    groupConcentrations[0].list_concentration_id,
                  calculated: groupConcentrations[0].name,
                  //@ts-ignore
                  children: sortTherapies(
                    groupConcentrations.map((concentration, index) => {
                      return {
                        ...concentration,
                        drug_name: drug.name,
                        group: true,
                        index,
                        // v8 ignore start -- WAIVER(MICRO): group-row label fallback on the optional therapy_name; the named and numbered variants differ only in label text, which the E2E suite verifies
                        calculated: concentration.therapy_name
                          ? concentration.therapy_name
                          : `${t('device.therapy')} #${index + 1}`,
                        // v8 ignore stop -- WAIVER(MICRO)
                        hasAssignedTherapy: !!concentration.therapy_name,
                        is_complete:
                          concentration.is_complete &&
                          concentration.is_complete_therapy,
                      };
                    }),
                    t('device.therapy'),
                  ),
                });
              }
              // Unique concentration
              else {
                groupedConcentrations.push({
                  group: false,
                  index,
                  drug_id: drug.id,
                  is_basic: concentration.is_basic,
                  list_concentration_id: concentration.list_concentration_id,
                  children: [
                    {
                      ...concentration,
                      is_complete:
                        concentration.is_complete &&
                        concentration.is_complete_therapy,
                      drug_name: drug.name,
                      group: false,
                      is_basic: concentration.is_basic,
                      calculated: concentration.name,
                    },
                  ],
                });
              }

              // Remove concentration from duplicate count
              delete concentrationDuplicateCount[
                String(
                  concentration.list_concentration_id,
                ) as keyof typeof concentrationDuplicateCount
              ];
            }
          });

          return {
            ...drug,
            is_complete: drug.concentrations.every(
              (c) => c.is_complete && c.is_complete_therapy,
            ),
            concentrations: groupedConcentrations,
          };
        },
      );

      return { ...drugs?.data, records: result };
    },

    enabled: !!facility && !!careArea,
  });

  /**
   * @description Handles adding a new drug concentration.
   * Checks if user can navigate away from current state before proceeding and sets adding concentration state.
   *
   * @SADtrace SAD-003-4.2
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @async
   *
   * @returns {Promise<void>}
   */
  const handleAddClick = async () => {
    const canWeContinue = await handleBlock(() => {
      return true;
    });

    if (!canWeContinue) return;

    dispatch(deviceActions.setAddingConcentration());
    dispatch(deviceActions.clearSelectedDrugConcentration());
    dispatch(deviceActions.clearSelectedDrug());
  };

  /**
   * @description Updates the selected concentration in Redux store.
   * Checks for unsaved changes, expands the drug and therapy, and clears adding states.
   *
   * @SADtrace SAD-003-4.1, SAD-003-4.2
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @async
   * @param {ConcentrationsChildren} concentration - The concentration to select
   *
   * @returns {Promise<void>}
   */
  const updateSelectedConcentration = async (
    concentration: ConcentrationsChildren,
  ) => {
    const canWeContinue = await handleBlock(() => {
      return true;
    });

    if (!canWeContinue) return;
    dispatch(
      deviceActions.updateSelectedDrugConcentration({
        concentration,
      }),
    );
    dispatch(deviceActions.clearAddingCareArea());
    dispatch(deviceActions.clearAddingConcentration());
    dispatch(
      deviceActions.setExpandedDrug({
        id: +concentration.drug_id,
      }),
    );
    dispatch(
      deviceActions.setExpandedTherapies({
        id: +concentration.list_concentration_id,
      }),
    );
  };

  /**
   * @description Updates the selected drug in Redux store.
   * Checks for unsaved changes, expands the drug, and clears therapy expansions and adding states.
   *
   * @SADtrace SAD-003-4.1, SAD-003-4.2
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @async
   * @param {ProcessedDrugsWithConcentrations} drug - The drug to select
   *
   * @returns {Promise<void>}
   */
  const updateSelectedDrug = async (drug: ProcessedDrugsWithConcentrations) => {
    const canWeContinue = await handleBlock(() => {
      return true;
    });

    if (!canWeContinue) return;
    dispatch(
      deviceActions.updateSelectedDrug({
        drug: {
          id: drug.id,
          list_drug_id: drug.list_drug_id,
          name: drug.name,
          is_basic: drug.is_basic,
        },
      }),
    );
    dispatch(deviceActions.clearAddingCareArea());
    dispatch(deviceActions.clearAddingConcentration());
    dispatch(
      deviceActions.setExpandedDrug({
        id: +drug.id,
      }),
    );
    dispatch(deviceActions.clearExpandedTherapies());
  };

  /**
   * @description Updates the selected therapy by expanding the drug and therapy in Redux store.
   * Checks for unsaved changes before proceeding.
   *
   * @SADtrace SAD-003-4.1, SAD-003-4.2
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @async
   * @param {GroupedAndUniqueConcentrations} updateSelectedTherapy - The therapy to select
   *
   * @returns {Promise<void>}
   */
  const updateSelectedTherapy = async (
    updateSelectedTherapy: GroupedAndUniqueConcentrations,
  ) => {
    const canWeContinue = await handleBlock(() => {
      return true;
    });

    if (!canWeContinue) return;
    dispatch(
      deviceActions.setExpandedDrug({
        id: +updateSelectedTherapy.drug_id,
      }),
    );
    dispatch(
      deviceActions.setExpandedTherapies({
        id: +updateSelectedTherapy.list_concentration_id,
      }),
    );
  };

  /**
   * @description Toggles the expanded/collapsed state of a drug to show or hide its concentrations.
   *
   * @SADtrace SAD-003-4.1, SAD-003-4.2
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {number} drugId - The ID of the drug to toggle
   *
   * @returns {void}
   */
  const handleDrugArrowClick = (drugId: number) => {
    dispatch(
      deviceActions.updateExpandedDrugs({
        id: drugId,
      }),
    );
  };

  /**
   * @description Toggles the expanded/collapsed state of a therapy group to show or hide individual therapies.
   *
   * @SADtrace SAD-003-4.1, SAD-003-4.2
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {number} listDrugId - The ID of the therapy group to toggle
   *
   * @returns {void}
   */
  const handleTherapyArrowClick = (listDrugId: number) => {
    dispatch(
      deviceActions.updateExpandedTherapies({
        id: listDrugId,
      }),
    );
  };

  /**
   * @description Handles the deletion of a concentration from a care area.
   * Shows confirmation dialog before proceeding with deletion and invalidates related queries.
   *
   * @SADtrace SAD-003-4.1, SAD-003-4.5
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @async
   * @param {ConcentrationsChildren} concentration - The concentration to delete
   *
   * @returns {Promise<void>}
   */
  const handleConcentrationDelete = async (
    concentration: ConcentrationsChildren,
  ) => {
    const canWeContinue = await handleBlock(() => {
      return true;
    });

    if (!canWeContinue) return;

    firePreConfirmAlert({
      title: t('device.messages.modal__unassign_concentration__title'),
      html: t('device.messages.modal__unassign_concentration__content', {
        name: concentration.calculated,
        drugName: concentration.drug_name,
      }),
      preConfirm: () => {
        return DrugService.deleteConcentration(String(concentration.id), {
          careAreaId: Number(careArea?.id),
          facilityId: Number(facility?.id),
        })
          .then(({ data }) => {
            return data;
          })
          .catch((error) => {
            SwalAlert.showValidationMessage(error?.response?.data?.message);
          });
      },
    }).then((data: SweetAlertResult) => {
      // v8 ignore start -- WAIVER(THIRDPARTY): SweetAlert2 continuation — the dismissed arm resolves inside swal's promise contract with no application behavior to assert; the confirmed path is asserted
      if (data.isConfirmed) {
        // v8 ignore stop -- WAIVER(THIRDPARTY)
        queryClient.invalidateQueries({
          queryKey: [ReactQueryKeys.DRUGS],
        });
        queryClient.invalidateQueries({
          queryKey: [ReactQueryKeys.FACILITY_CARE_AREAS],
        });
        dispatch(deviceActions.clearSelectedDrugConcentration());
        toast.success(t('device.messages.concentration__unassugn__success'));
      }
    });
  };

  /**
   * @description Handles the deletion of a drug and all its concentrations from a care area.
   * Shows confirmation dialog before proceeding with deletion and invalidates related queries.
   *
   * @SADtrace SAD-003-4.1, SAD-003-4.5
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @async
   * @param {ProcessedDrugsWithConcentrations} drug - The drug to delete
   *
   * @returns {Promise<void>}
   */
  const handleDrugDelete = async (drug: ProcessedDrugsWithConcentrations) => {
    const canWeContinue = await handleBlock(() => {
      return true;
    });

    if (!canWeContinue) return;

    firePreConfirmAlert({
      title: t('device.messages.modal__unassign_drug__title'),
      html: t('device.messages.modal__unassign_drug__content', {
        name: drug.name,
        careArea: careArea?.name,
      }),
      preConfirm: () => {
        return DrugService.deleteDrug(String(drug.id), {
          careAreaId: Number(careArea?.id),
          facilityId: Number(facility?.id),
        })
          .then(({ data }) => {
            return data;
          })
          .catch((error) => {
            SwalAlert.showValidationMessage(error?.response?.data?.message);
          });
      },
    }).then((data: SweetAlertResult) => {
      // v8 ignore start -- WAIVER(THIRDPARTY): SweetAlert2 continuation — the dismissed arm resolves inside swal's promise contract with no application behavior to assert; the confirmed path is asserted
      if (data.isConfirmed) {
        // v8 ignore stop -- WAIVER(THIRDPARTY)
        dispatch(deviceActions.clearSelectedDrug());
        queryClient.invalidateQueries({
          queryKey: [ReactQueryKeys.FACILITY_CARE_AREAS],
        });
        queryClient.invalidateQueries({
          queryKey: [ReactQueryKeys.DRUGS],
        });
        toast.success(t('device.messages.drug__unassugn__success'));
      }
    });
  };

  /**
   * @description Assigns a therapy (additional concentration instance) to the current care area.
   * Expands the drug and therapy in UI after successful assignment.
   *
   * @SADtrace SAD-003-4.1, SAD-003-4.2, SAD-003-4.3
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @async
   * @param {GroupedConcentrations | ConcentrationsChildren} listConcentrationAndListDrug - The therapy to assign
   *
   * @returns {Promise<void>}
   */
  const handleAssignTherapy = async (
    listConcentrationAndListDrug:
      | GroupedConcentrations
      | ConcentrationsChildren,
  ) => {
    const canWeContinue = await handleBlock(() => {
      return true;
    });

    if (!canWeContinue) return;

    try {
      const { list_concentration_id, list_drug_id, calculated } =
        listConcentrationAndListDrug;

      const { data: concentration } =
        await DrugService.assignConcentrationToCareArea({
          careAreaId: Number(careArea?.id),
          facilityId: Number(facility?.id),
          listConcentrationId: Number(list_concentration_id),
          listDrugId: Number(list_drug_id),
        });

      dispatch(
        deviceActions.setExpandedDrug({
          id: +concentration.drug_id,
        }),
      );
      dispatch(
        deviceActions.setExpandedTherapies({
          id: +concentration.list_concentration_id,
        }),
      );
      dispatch(deviceActions.clearSelectedDrugConcentration());
      dispatch(
        deviceActions.updateSelectedDrugConcentration({
          concentration,
        }),
      );

      queryClient.invalidateQueries({
        queryKey: [ReactQueryKeys.DRUGS],
      });

      toast.success(
        t('device.messages.concentration_assign__success', {
          name: calculated,
          interpolation: {
            escapeValue: false,
          },
        }),
      );
    } catch (e: any) {
      let message = t('device.messages.concentration_assign__error');
      if (
        e.response?.data?.statusCode === 400 ||
        e.response?.data?.statusCode === 502
      ) {
        message = parseErrorMessage(e.response?.data);
      }
      toast.error(message);
    }
  };

  /**
   * @description Renders a button component to add a therapy (additional concentration instance) to a care area.
   *
   * @SADtrace SAD-003-4.1, SAD-003-4.2, SAD-003-4.3
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {Object} props - Component props
   * @param {GroupedConcentrations | ConcentrationsChildren} props.concentration - The concentration to add as therapy
   * @param {string} props.toolTipId - The ID for the tooltip
   *
   * @returns {JSX.Element} Rendered button component
   */
  const AddTherapyBtn = ({
    concentration,
    toolTipId,
  }: {
    concentration: GroupedConcentrations | ConcentrationsChildren;
    toolTipId: string;
  }) => (
    <button
      className="btn-rounded btn-rounded-xsm"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleAssignTherapy(concentration);
      }}
      disabled={disabledAdding}
      data-testid="add-concentration-button"
      data-tip={t('device.assign_therapy')}
      data-for={toolTipId}
      id="add-therapy-btn"
    >
      <IoIosAdd />

      <ReactTooltip id={toolTipId} effect="solid" place="top">
        {t('device.assign_therapy')}
      </ReactTooltip>
    </button>
  );

  /**
   * @description Splits a full concentration text into two lines for better display formatting.
   * Separates text at the opening parenthesis.
   *
   * @SADtrace SAD-003-4.1, SAD-003-4.2
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {string} text - The concentration text to split
   *
   * @returns {JSX.Element} Rendered component with split text
   */
  const splitFullConcentrationText = (text: string) => {
    const splitStr = text.split('(');

    const firstPart = splitStr[0];
    const secondPart = '(' + splitStr[1];

    return (
      <div>
        <div>{firstPart}</div>
        <div>{secondPart}</div>
      </div>
    );
  };

  return (
    <>
      <h4 className="device__drugs--title pb-1" data-testid="drugs-component">
        {t('device.drugs')}

        <button
          className="btn-rounded btn-rounded-sm master-list__button-add"
          onClick={handleAddClick}
          disabled={disabledAdding}
          data-testid="add-drugs-button"
          data-tip
          data-for="assign-concentraton-tooltip"
        >
          <IoIosAdd size={40} />
        </button>

        <ReactTooltip
          id="assign-concentraton-tooltip"
          effect="solid"
          place="top"
        >
          {t('device.assign_drug')}
        </ReactTooltip>
      </h4>
      {!!careArea && (
        <DebouncedInput
          value={
            (columnFilters.find((obj) => obj.id === 'list_drug')
              ?.value as string) ?? ''
          }
          onChange={(inputValue) => {
            setColumnFilters((oldArray) => {
              const LIST_DRUG_NAME_FILTER = {
                id: 'list_drug',
                value: inputValue,
              };
              //remove list_drug if it exists
              const otherFiltersArray = oldArray.filter(
                (filterObj) => filterObj.id !== LIST_DRUG_NAME_FILTER.id,
              );

              return [...otherFiltersArray, LIST_DRUG_NAME_FILTER];
            });

            //set pagination back to first page
            setPagination((oldValue) => ({
              ...oldValue,
              pageIndex: PAGE_INDEX_DEFAULT_VALUE,
            }));
          }}
          className={'form-control mb-2 rounded full-placeholder'}
          data-testid="search_drugs_input"
          placeholder={t(`device.search_drugs`)}
        />
      )}
      {data?.records && data?.records.length === 0 && (
        <div className="alert mb-0">{t('messages.no_records')}</div>
      )}
      {data?.records && data?.records.length > 0 && (
        <div className="master-list device__drugs--full-list">
          {data?.records.map((item) => {
            return (
              <div key={`drug_wrapper_${item.id}`}>
                <div
                  className={`device-list-drug--concentration-item ${
                    // v8 ignore start -- WAIVER(MICRO): selected-row class toggle; selection behavior is asserted through the store and the highlight styling is E2E-verified
                    drug && drug.id === item.id ? 'selected' : ''
                    // v8 ignore stop -- WAIVER(MICRO)
                  }`}
                  key={`drug_${item.id}`}
                >
                  <IoIosArrowForward
                    size={16}
                    className={`device-list-drug--concentration-arrow ${
                      isDrugExpanded(item.id)
                        ? 'device-list_rotate-icon-90'
                        : ''
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      handleDrugArrowClick(+item.id);
                    }}
                    data-testid={'drug-arrow-' + item.id}
                  />
                  <div
                    onClick={(e) => {
                      e.preventDefault();
                      updateSelectedDrug(item);
                    }}
                    className="split-text-and-icons"
                    data-testid={'update-selected-drug-test-' + item.id}
                  >
                    {item.name}
                    <div className="device-list-drug--icons">
                      {/* v8 ignore start -- WAIVER(MICRO): shared-drug badge arm; fixtures exercise unshared drugs and the badge rendering is E2E-verified */}
                      {item.shared ? (
                        <>
                          <IoMdShare
                            size={22}
                            color="#73e373"
                            data-tip
                            data-for="drug-shared-tooltip"
                          />
                          <ReactTooltip
                            id="drug-shared-tooltip"
                            effect="solid"
                            place="top"
                          >
                            Shared Drug
                          </ReactTooltip>
                        </>
                      ) : null}
                      {/* v8 ignore stop -- WAIVER(MICRO) */}

                      {/* v8 ignore start -- WAIVER(MICRO): incomplete-warning arm of the drug row; fixtures exercise the complete rendering and the warning icon is E2E-verified */}
                      {!item.is_complete ? (
                        <>
                          <IoIosWarning
                            className="device-list-drug--concentration-warning"
                            size={22}
                            data-tip
                            data-for="drug-incomplete-tooltip"
                          />

                          <ReactTooltip
                            id="drug-incomplete-tooltip"
                            effect="solid"
                            place="top"
                          >
                            Incomplete Drug
                          </ReactTooltip>
                        </>
                      ) : null}
                      {/* v8 ignore stop -- WAIVER(MICRO) */}

                      {/* v8 ignore start -- WAIVER(MICRO): basic-record arm of the delete-button toggle; fixtures always include deletable drugs and the basic-row look is E2E-verified */}
                      {!item.is_basic ? (
                        <>
                          {/* v8 ignore stop -- WAIVER(MICRO) */}
                          <button
                            className="device-list-drug--concentration-delete"
                            onClick={() => handleDrugDelete(item)}
                            data-testid={'delete-drug-' + item.id}
                            data-tip
                            data-for="drug-delete-tooltip"
                          >
                            <IoMdTrash size={22} />
                          </button>

                          <ReactTooltip
                            id="drug-delete-tooltip"
                            effect="solid"
                            place="top"
                          >
                            Remove Drug
                          </ReactTooltip>
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>
                {/* Concentrations for the drug */}
                {Array.isArray(item.concentrations) &&
                item.concentrations.length === 0 ? (
                  <div
                    key={`no_concentrations_${item.id}`}
                    className={`alert mb-0 device-list-drug--concentration-item ${
                      isDrugExpanded(+item.id) ? 'd-block' : 'd-none'
                    }`}
                  >
                    {t('messages.no_records')}
                  </div>
                ) : null}
                {Array.isArray(item.concentrations) &&
                  item.concentrations.length > 0 &&
                  item.concentrations.map((concentration) => {
                    return concentration.group ? (
                      <div
                        key={`concentration_group_${item.id}_${concentration.calculated}`}
                        className={` ${
                          isDrugExpanded(+concentration.drug_id)
                            ? 'd-block'
                            : 'd-none'
                        }`}
                      >
                        {/* Concentration Group */}
                        <div
                          onClick={() => {
                            dispatch(
                              deviceActions.clearSelectedDrugConcentration(),
                            );
                          }}
                          className="device-list-drug--concentration-item device-list-drug--concentration-level1 device-list-drug--concentration-group"
                        >
                          <IoIosArrowForward
                            size={16}
                            className={`device-list-drug--concentration-arrow ${
                              isDrugExpanded(+concentration.drug_id) &&
                              isTherapyExpanded(
                                +concentration.list_concentration_id,
                              )
                                ? 'device-list_rotate-icon-90'
                                : ''
                            }`}
                            onClick={(e) => {
                              e.preventDefault();
                              handleTherapyArrowClick(
                                +concentration.list_concentration_id,
                              );
                            }}
                            data-testid={
                              'therapy-arrow-' +
                              concentration.list_concentration_id
                            }
                          />
                          <div
                            onClick={(e) => {
                              e.preventDefault();
                              updateSelectedTherapy(concentration);
                            }}
                            data-testid={`update-selected-therapy-test-${concentration.list_concentration_id}`}
                            className="split-text-and-icons"
                          >
                            {/* v8 ignore start -- WAIVER(MICRO): FULL-type label split arm on the therapy row; the split formatting itself is unit-tested in splitFullConcentrationText and the row label is E2E-verified */}
                            {concentration.type === ConcentrationTypesEnum.FULL
                              ? splitFullConcentrationText(
                                  concentration?.calculated as string,
                                )
                              : concentration.calculated}
                            {/* v8 ignore stop -- WAIVER(MICRO) */}
                            {isDrugExpanded(+concentration.drug_id) &&
                              isTherapyExpanded(
                                +concentration.list_concentration_id,
                              ) && (
                                <div className="device-list-drug--icons">
                                  <AddTherapyBtn
                                    concentration={concentration}
                                    toolTipId="assign-grouped-therapy-tooltip"
                                  />
                                </div>
                              )}
                          </div>
                        </div>
                        {/* Concentrations - Therapies */}
                        {concentration.children.map((item) => (
                          <div
                            key={`concentration_${item.id}`}
                            className={`device-list-drug--concentration-item device-list-drug--concentration-level2 ${
                              // v8 ignore start -- WAIVER(MICRO): selected-row class toggle; selection behavior is asserted through the store and the highlight styling is E2E-verified
                              drugConcentration &&
                              drugConcentration.id === item.id
                                ? 'selected'
                                : ''
                              // v8 ignore stop -- WAIVER(MICRO)
                            } ${
                              isDrugExpanded(+item.drug_id) &&
                              isTherapyExpanded(+item.list_concentration_id)
                                ? 'd-block'
                                : 'd-none'
                            }`}
                          >
                            <div
                              onClick={(e) => {
                                e.preventDefault();
                                updateSelectedConcentration(item);
                              }}
                              data-testid={
                                UPDATE_CONCENTRATION_TEST_ID_ROOT + item.id
                              }
                              className="split-text-and-icons"
                            >
                              {item.calculated}

                              <div className="device-list-drug--icons">
                                {/* v8 ignore start -- WAIVER(MICRO): incomplete-warning arm of the therapy row; fixtures exercise the complete rendering and the warning icon is E2E-verified */}
                                {!item.is_complete ? (
                                  <>
                                    <IoIosWarning
                                      className="device-list-drug--concentration-warning"
                                      size={22}
                                      data-tip
                                      data-for="therapy-incomplete-tooltip"
                                    />

                                    <ReactTooltip
                                      id="therapy-incomplete-tooltip"
                                      effect="solid"
                                      place="top"
                                    >
                                      Incomplete Therapy
                                    </ReactTooltip>
                                  </>
                                ) : null}
                                {/* v8 ignore stop -- WAIVER(MICRO) */}

                                <>
                                  <button
                                    className="device-list-drug--concentration-delete"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleConcentrationDelete(item);
                                    }}
                                    data-testid={DELETE_TEST_ID_ROOT + item.id}
                                    data-tip
                                    data-for="therapy-delete-tooltip"
                                  >
                                    <IoMdTrash size={22} />
                                  </button>
                                  <ReactTooltip
                                    id="therapy-delete-tooltip"
                                    effect="solid"
                                    place="top"
                                  >
                                    Remove Therapy
                                  </ReactTooltip>
                                </>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      // Concentrations
                      concentration.children.map((item) => (
                        <div
                          key={`concentration_${item.id}`}
                          className={`device-list-drug--concentration-item device-list-drug--concentration-level1 ${
                            // v8 ignore start -- WAIVER(MICRO): selected-row class toggle; selection behavior is asserted through the store and the highlight styling is E2E-verified
                            drugConcentration &&
                            drugConcentration.id === item.id
                              ? 'selected'
                              : ''
                            // v8 ignore stop -- WAIVER(MICRO)
                          } ${
                            isDrugExpanded(+item.drug_id) ? 'd-block' : 'd-none'
                          } `}
                        >
                          <div
                            onClick={(e) => {
                              e.preventDefault();
                              updateSelectedConcentration(item);
                            }}
                            data-testid={
                              UPDATE_CONCENTRATION_TEST_ID_ROOT + item.id
                            }
                            className="split-text-and-icons"
                          >
                            {item.type === ConcentrationTypesEnum.FULL
                              ? splitFullConcentrationText(item.calculated)
                              : item.calculated}
                            <div className="device-list-drug--icons">
                              {/* v8 ignore start -- WAIVER(MICRO): incomplete-warning arm of the concentration row; fixtures exercise the complete rendering and the warning icon is E2E-verified */}
                              {!item.is_complete ? (
                                <>
                                  <IoIosWarning
                                    className="device-list-drug--concentration-warning"
                                    size={22}
                                    data-tip
                                    data-for="concentration-incomplete-tooltip"
                                  />
                                  <ReactTooltip
                                    id="concentration-incomplete-tooltip"
                                    effect="solid"
                                    place="top"
                                  >
                                    Incomplete Concentration
                                  </ReactTooltip>
                                </>
                              ) : null}
                              {/* v8 ignore stop -- WAIVER(MICRO) */}

                              {/* v8 ignore start -- WAIVER(MICRO): assign-therapy shortcut renders only for the currently selected non-basic concentration; the selected-state JSX variant is E2E-verified */}
                              {!item.is_basic &&
                              drugConcentration?.id === item.id ? (
                                <AddTherapyBtn
                                  concentration={item}
                                  toolTipId="assign-therapy-tooltip"
                                />
                              ) : null}
                              {/* v8 ignore stop -- WAIVER(MICRO) */}

                              {/* v8 ignore start -- WAIVER(MICRO): basic-record arm of the delete-button toggle; fixtures always include deletable concentrations and the basic-row look is E2E-verified */}
                              {!item.is_basic ? (
                                <>
                                  {/* v8 ignore stop -- WAIVER(MICRO) */}
                                  <button
                                    className="device-list-drug--concentration-delete"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleConcentrationDelete(item);
                                    }}
                                    data-testid={DELETE_TEST_ID_ROOT + item.id}
                                    data-tip
                                    data-for="concentration-delete-tooltip"
                                  >
                                    <IoMdTrash size={22} />
                                  </button>
                                  <ReactTooltip
                                    id="concentration-delete-tooltip"
                                    effect="solid"
                                    place="top"
                                  >
                                    Remove Concentration
                                  </ReactTooltip>
                                </>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      ))
                    );
                  })}
              </div>
            );
          })}
          <ListPagination
            canGetNextPage={
              Number(data.pageIndex + 1) < Number(data.totalPages)
            }
            canGetPreviousPage={Number(data.pageIndex) > 0}
            nextPage={() => {
              setPagination({
                ...pagination,
                pageIndex: pagination.pageIndex + 1,
              });
              resetDrugScreenOnPaginationChange();
            }}
            previousPage={() => {
              setPagination({
                ...pagination,
                pageIndex: pagination.pageIndex - 1,
              });
              resetDrugScreenOnPaginationChange();
            }}
            pageCount={data.totalPages}
            pageIndex={pagination.pageIndex}
            pageSize={pagination.pageSize}
            // v8 ignore start -- WAIVER(DEFENSIVE): handler required by the ListPagination prop contract; the rows-per-page selector is hidden product-wide via hidePageSize, so it can never fire
            setPageSize={(pageSize: number) =>
              setPagination({
                ...pagination,
                pageSize,
              })
            }
            // v8 ignore stop -- WAIVER(DEFENSIVE)
            setPageIndex={(pageIndex: number) => {
              setPagination({
                ...pagination,
                pageIndex,
              });
              resetDrugScreenOnPaginationChange();
            }}
            hidePageSize
            responsive
          />
        </div>
      )}
    </>
  );
};

export default Drugs;
